import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react'
import { EntityRef } from '../../components/EntityRef'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { StatusPill } from '../../components/StatusPill'
import { UserPicker, type UserPickerValue } from '../../components/UserPicker'
import { HelpBalloon } from '../../help/HelpBalloon'
import type { HelpContentId } from '../../help/helpContent'
import { getProduction, listSpendLines } from '../../api/film'
import { listEvidenceItems } from '../../api/evidence'
import { listCaseTeamMembers } from '../../api/caseTeam'
import { assignCaseOwner, getCaseOwner } from '../../api/caseOwner'
import { getCreditInstrumentByFiling } from '../../api/credit'
import { getTransferByInstrument } from '../../api/monetization'
import { listAuthorityInteractions } from '../../api/authority'
import { listCaseDossiers } from '../../api/dossier'
import { listCaseTasks } from '../../api/tasks'
import { getLegalEntity } from '../../api/legalEntities'
import { getUiConfig } from '../../api/identity'
import { useProgramRefs } from '../../lib/useProgramRefs'
import { useCaseContexts } from '../tasks/useCaseContexts'
import { label } from '../../lib/labels'
import { deriveLifecycle } from './lifecycle'
import { LifecycleTracker } from './LifecycleTracker'
import { deriveCaseProgress } from './caseProgress'
import { CaseProgressIndicator } from './CaseProgressIndicator'
import type { CaseView } from '../../types/case'
import type { CaseOwnerView } from '../../types/caseOwner'
import type { ProgramFilingDetailView } from '../../types/programFilingDetail'
import type { DossierSummaryView } from '../../types/dossier'

/** IB-336 — reuses the `case.team-manage` UI-component gate `TeamPanel` already establishes: the
 * SAME `case:W` roster-mutation authority governs Case Owner assignment (IB-335 tech-spec — "no
 * bespoke object introduced"), so the client-side hide reuses the identical key rather than
 * inventing a second one for the same server-side gate. */
const CASE_OWNER_MANAGE_COMPONENT = 'case.team-manage'

/** The Case Owner row + its "Assign/Change owner" affordance (IB-335/IB-336) — a second, independent
 * `UserPicker` consumer beyond the Case Team tab, proving the picker is genuinely reusable. A masked
 * read (`ownerUserId: null` for a caller who cannot see the owner, FLS "hidden" semantics) renders
 * identically to "no owner assigned yet" — this component never distinguishes the two, by design. */
function CaseOwnerRow({ caseId }: { caseId: string }) {
  const [owner, setOwner] = useState<CaseOwnerView | null>(null)
  const [canManage, setCanManage] = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  const [picked, setPicked] = useState<UserPickerValue | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(() => {
    getCaseOwner(caseId)
      .then(setOwner)
      .catch(() => setOwner(null))
    getUiConfig()
      .then((config) => setCanManage(config.components.includes(CASE_OWNER_MANAGE_COMPONENT)))
      .catch(() => setCanManage(false))
  }, [caseId])

  useEffect(() => {
    load()
  }, [load])

  async function handleAssign() {
    if (!picked) return
    setSaving(true)
    setError(null)
    try {
      const updated = await assignCaseOwner(caseId, picked.id)
      setOwner(updated)
      setModalOpen(false)
      setPicked(null)
    } catch {
      setError('Could not assign the case owner.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <SummaryRow term="Case owner">
      <span style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
        {owner?.ownerName ?? 'Unassigned'}
        {canManage ? (
          <button type="button" className="iw-btn iw-btn--sm" onClick={() => setModalOpen(true)}>
            {owner?.ownerUserId ? 'Change' : 'Assign'}
          </button>
        ) : null}
      </span>
      {modalOpen ? (
        <FocusTrapDialog titleId="case-owner-title" title="Assign case owner" onClose={() => setModalOpen(false)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
            {error ? <p style={{ color: 'var(--iw-danger)', margin: 0 }}>{error}</p> : null}
            <UserPicker id="case-owner-picker" label="Owner" value={picked} onChange={setPicked} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--iw-space-2)' }}>
              <button type="button" className="iw-btn" onClick={() => setModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="iw-btn iw-btn--primary"
                disabled={!picked || saving}
                onClick={() => void handleAssign()}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </FocusTrapDialog>
      ) : null}
    </SummaryRow>
  )
}

export interface OverviewPanelProps {
  caseView: CaseView
  filings: ProgramFilingDetailView[]
}

interface OverviewStats {
  productionTitle: string | null
  productionType: string | null
  legalEntityJurisdiction: string | null
  totalQualifiedSpend: number | null
  qualifiedSpendCurrency: string | null
  spendLineCount: number | null
  evidenceCount: number | null
  teamCount: number | null
  taskCount: number | null
  authorityInteractionCount: number | null
  certifiedCreditTotal: number | null
  certifiedCreditCurrency: string | null
  transferredTotal: number | null
  transferredCurrency: string | null
  /**
   * Raw, per-instance arrays (as opposed to the aggregated totals above) — the exact inputs
   * `deriveLifecycle` (Case Lifecycle Progress Tracker, lifecycle.ts) needs. No new fetch: the
   * instruments/transfers below are the very same `getCreditInstrumentByFiling`/
   * `getTransferByInstrument` reads this effect already makes for the stat tiles; `dossiers` is the
   * ONE genuinely new read this story adds (`GET /cases/{caseId}/dossiers`, film-only, mirroring the
   * Dossier tab's own gating), degrading to `[]` on any failure like every other tile here.
   */
  certifiedInstruments: { certifiedAmount: number; currency: string }[]
  transfers: { salePrice: number; currency: string | null }[]
  dossiers: { status: string; version: number; finalizedAt: string | null }[]
}

const EMPTY_STATS: OverviewStats = {
  productionTitle: null,
  productionType: null,
  legalEntityJurisdiction: null,
  totalQualifiedSpend: null,
  qualifiedSpendCurrency: null,
  spendLineCount: null,
  evidenceCount: null,
  teamCount: null,
  taskCount: null,
  authorityInteractionCount: null,
  certifiedCreditTotal: null,
  certifiedCreditCurrency: null,
  transferredTotal: null,
  transferredCurrency: null,
  certifiedInstruments: [],
  transfers: [],
  dossiers: [],
}

function formatAmount(amount: number, currency: string | null): string {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency ?? 'USD' }).format(amount)
  } catch {
    return `${currency ?? ''} ${amount.toFixed(2)}`.trim()
  }
}

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString()
}

/**
 * One labeled stat tile (design tokens only — never a literal hex, CLAUDE.md §4). `subLabel` is a
 * short qualifier (e.g. "(transferred)") rendered on its own line, one size down — this keeps the
 * primary label itself always a single, non-wrapping line at the tile's normal width (bug #17: a
 * long combined label like "Net proceeds (transferred)" used to wrap mid-word onto two lines).
 */
function StatCard({
  label: cardLabel,
  subLabel,
  value,
  helpId,
}: {
  label: string
  subLabel?: string
  value: string
  /** Optional — only the tiles worth a quirk-callout (e.g. Net proceeds) carry one; most stat
   * tiles are self-explanatory and a balloon on every one would be noise, not help. */
  helpId?: HelpContentId
}) {
  return (
    <div
      style={{
        flex: '1 1 160px',
        minWidth: 160,
        padding: 'var(--iw-space-3)',
        background: 'var(--iw-neutral-100)',
        border: '1px solid var(--iw-border)',
        borderRadius: 'var(--iw-radius)',
        // Column layout + value pinned to the bottom (marginTop:auto below) so every tile's value
        // sits on the same baseline across the row regardless of how many lines its label needs
        // (bug: a two-line label like "Net proceeds / (transferred)" pushed its value down and out
        // of alignment with the single-line-label tiles). Row tiles already stretch to equal height
        // (flex default), so bottom-aligning the value guarantees horizontal alignment.
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          fontSize: 'var(--iw-size-xs)',
          color: 'var(--iw-text-muted)',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--iw-space-1)',
        }}
        title={subLabel ? `${cardLabel} ${subLabel}` : undefined}
      >
        {cardLabel}
        {helpId ? <HelpBalloon id={helpId} /> : null}
      </div>
      {subLabel ? (
        <div style={{ fontSize: 'var(--iw-size-xs)', color: 'var(--iw-text-subtle)', whiteSpace: 'nowrap' }}>
          {subLabel}
        </div>
      ) : null}
      <div style={{ fontSize: 'var(--iw-size-lg)', fontWeight: 'var(--iw-weight-medium)' as never, marginTop: 'auto' }}>{value}</div>
    </div>
  )
}

/** One row of a definition list (case-summary block) — a label/value pair, never a raw UUID for the value. */
function SummaryRow({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: 'var(--iw-space-2)', fontSize: 'var(--iw-size-xs)' }}>
      <dt style={{ minWidth: 120, color: 'var(--iw-text-muted)' }}>{term}</dt>
      <dd style={{ margin: 0 }}>{children}</dd>
    </div>
  )
}

const FILING_STATE_ORDER_VARIANT: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'neutral' | 'muted'> = {
  filed: 'success',
  certify: 'success',
  monetize: 'success',
  audit: 'warning',
  amend: 'warning',
  review: 'info',
  cpa: 'info',
  produce: 'info',
  file: 'info',
  prepare: 'neutral',
  reserve: 'neutral',
}

/**
 * The "where is this case in the lifecycle" strip (case-enrichment story) — one chip per
 * `ProgramFiling` showing its cartridge `Lifecycle` state, its Program (never a bare UUID, via
 * {@link useProgramRefs}), and — once recorded — its outcome (`submissionOutcome`) and filed date.
 * Purely a richer read over fields `ProgramFilingDetailView` already carries; no new fetch.
 */
function LifecycleStrip({
  filings,
  programRefs,
}: {
  filings: ProgramFilingDetailView[]
  programRefs: Map<string, { display: string }>
}) {
  if (filings.length === 0) {
    return <p style={{ color: 'var(--iw-text-muted)', margin: 0 }}>No program filings yet for this case.</p>
  }
  return (
    <ul
      style={{
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--iw-space-2)',
      }}
    >
      {filings.map((f) => (
        <li
          key={f.id}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--iw-space-3)',
            flexWrap: 'wrap',
            padding: 'var(--iw-space-2) 0',
            borderBottom: '1px solid var(--iw-border)',
          }}
        >
          <EntityRef kind="program" id={f.programId} name={programRefs.get(f.programId)?.display} />
          <StatusPill
            variant={FILING_STATE_ORDER_VARIANT[f.currentState] ?? 'neutral'}
            label={label('programFilingState', f.currentState)}
          />
          {f.outcomeStatus ? (
            <StatusPill variant="info" label={label('programFilingOutcomeStatus', f.outcomeStatus)} />
          ) : null}
          {f.submissionOutcome ? (
            <StatusPill
              variant={f.submissionOutcome === 'denied' ? 'danger' : f.submissionOutcome === 'approved' ? 'success' : 'warning'}
              label={label('submissionOutcome', f.submissionOutcome)}
            />
          ) : null}
          {f.filedAt ? (
            <span style={{ color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>
              Filed {formatDate(f.filedAt)}
            </span>
          ) : null}
          {f.approvedAmount !== null ? (
            <span style={{ color: 'var(--iw-text-subtle)', fontSize: 'var(--iw-size-xs)' }}>
              Approved {formatAmount(f.approvedAmount, f.approvedCurrency)}
            </span>
          ) : null}
        </li>
      ))}
    </ul>
  )
}

/**
 * The enriched Overview tab (GA demo v4, further enriched by the case-enrichment story) — a case
 * summary (Account/Legal entity/Production/Fiscal year, never a bare UUID), a lifecycle strip
 * (every ProgramFiling's state + outcome), a headline-numbers stat row, and the existing
 * Program-filings list. Every figure here is read-only, assembled client-side from already-
 * published GET endpoints — never a second write path, never a guarded field rendered as editable.
 *
 * Each stat is fetched independently (`Promise.allSettled`) so ONE failing/absent source (e.g. an
 * R&D case has no `productionId`, so no spend/production fetch runs at all; a case with no
 * certified filing yet has no CreditInstrument) degrades that single tile to "—", never blanks the
 * whole tab — the caller (`CaseDetailScreen`) additionally wraps this panel in a
 * `WidgetErrorBoundary` as defense-in-depth against an unexpected render throw.
 */
export function OverviewPanel({ caseView, filings }: OverviewPanelProps) {
  const [stats, setStats] = useState<OverviewStats>(EMPTY_STATS)
  const [loading, setLoading] = useState(true)
  const programRefs = useProgramRefs(filings.map((f) => f.programId))
  const caseContexts = useCaseContexts([caseView.id])
  const caseContext = caseContexts.get(caseView.id)

  useEffect(() => {
    let cancelled = false
    setLoading(true)

    async function load() {
      // Every stat is independently degrade-able (one absent/failing source never blanks the
      // whole tile row) — the outer try/catch is a last-resort backstop only, so an unexpected
      // shape (or a request settling after this effect's own cleanup/unmount) degrades this
      // render pass to "whatever partial stats resolved" rather than an unhandled rejection
      // escaping the effect.
      const next: OverviewStats = { ...EMPTY_STATS }
      try {
        if (caseView.kind === 'film' && caseView.productionId) {
          const productionId = caseView.productionId
          const [productionResult, spendResult] = await Promise.allSettled([
            getProduction(productionId),
            listSpendLines(productionId),
          ])
          if (productionResult.status === 'fulfilled' && productionResult.value) {
            next.productionTitle = productionResult.value.title ?? null
            next.productionType = productionResult.value.type ?? null
            const totals = (productionResult.value.productionFys ?? []).filter(
              (fy) => fy.qualifiedSpendTotal !== null,
            )
            if (totals.length > 0) {
              next.totalQualifiedSpend = totals.reduce((sum, fy) => sum + (fy.qualifiedSpendTotal ?? 0), 0)
              next.qualifiedSpendCurrency = totals[0]?.qualifiedSpendTotalCurrency ?? null
            }
          }
          if (spendResult.status === 'fulfilled' && spendResult.value) {
            next.spendLineCount = spendResult.value.length
            if (next.totalQualifiedSpend === null && spendResult.value.length > 0) {
              // Fallback: the guarded, computed ProductionFY roll-up (above) is not populated yet
              // — sum the raw spend-line amounts instead, a plain display aggregate, never
              // written back.
              next.totalQualifiedSpend = spendResult.value.reduce((sum, line) => sum + line.amount, 0)
              next.qualifiedSpendCurrency = spendResult.value[0]?.currency ?? null
            }
          }
        }

        const legalEntityResult = await getLegalEntity(caseView.legalEntityId).catch(() => null)
        if (legalEntityResult) {
          next.legalEntityJurisdiction = legalEntityResult.entity.jurisdiction ?? null
        }

        const [evidenceResult, teamResult, taskResult, creditResults, dossierResult] = await Promise.allSettled([
          listEvidenceItems(caseView.id),
          listCaseTeamMembers(caseView.id),
          listCaseTasks(caseView.id),
          Promise.allSettled(filings.map((f) => getCreditInstrumentByFiling(f.id))),
          // Dossier only exists for film cases (mirrors the Dossier tab's own `kind === 'film'`
          // gate on this same screen) — a non-film case never makes this call at all, rather than
          // fetching and discarding a guaranteed-empty result.
          caseView.kind === 'film' ? listCaseDossiers(caseView.id) : Promise.resolve([] as DossierSummaryView[]),
        ])
        if (evidenceResult.status === 'fulfilled' && evidenceResult.value) {
          next.evidenceCount = evidenceResult.value.items.length
        }
        if (teamResult.status === 'fulfilled' && teamResult.value) {
          next.teamCount = teamResult.value.items.filter((m) => m.active).length
        }
        if (taskResult.status === 'fulfilled' && taskResult.value) {
          next.taskCount = taskResult.value.items.length
        }
        if (dossierResult.status === 'fulfilled') {
          next.dossiers = dossierResult.value.map((d) => ({
            status: d.status,
            version: d.version,
            finalizedAt: d.finalizedAt,
          }))
        }
        let instruments: NonNullable<Awaited<ReturnType<typeof getCreditInstrumentByFiling>>>[] = []
        if (creditResults.status === 'fulfilled') {
          instruments = creditResults.value
            .filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof getCreditInstrumentByFiling>>> =>
              r.status === 'fulfilled')
            .map((r) => r.value)
            .filter((v): v is NonNullable<typeof v> => v !== null && v !== undefined)
          if (instruments.length > 0) {
            next.certifiedCreditTotal = instruments.reduce((sum, i) => sum + i.certifiedAmount, 0)
            next.certifiedCreditCurrency = instruments[0]?.currency ?? null
            next.certifiedInstruments = instruments.map((i) => ({
              certifiedAmount: i.certifiedAmount,
              currency: i.currency,
            }))
          }
        }

        // Transfer economics and Authority-interaction counts mirror this same screen's own
        // Monetization/Authority tabs' film-only scoping (both `TransferTxn`/`AuthorityInteraction`
        // reads are exercised there already) — a non-film case simply never fetches either, so its
        // stat row omits those two tiles rather than showing a fabricated zero.
        if (caseView.kind === 'film' && instruments.length > 0) {
          const transferResults = await Promise.allSettled(
            instruments.map((i) => getTransferByInstrument(i.id)),
          )
          const transfers = transferResults
            .filter((r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof getTransferByInstrument>>> =>
              r.status === 'fulfilled')
            .map((r) => r.value)
            .filter((v): v is NonNullable<typeof v> => v !== null && v !== undefined)
          if (transfers.length > 0) {
            next.transferredTotal = transfers.reduce((sum, t) => sum + t.salePrice, 0)
            next.transferredCurrency = next.certifiedCreditCurrency
            next.transfers = transfers.map((t) => ({ salePrice: t.salePrice, currency: next.certifiedCreditCurrency }))
          }
        }
        if (caseView.kind === 'film' && filings.length > 0) {
          const interactionResults = await Promise.allSettled(
            filings.map((f) => listAuthorityInteractions(f.id)),
          )
          const fulfilled = interactionResults.filter(
            (r): r is PromiseFulfilledResult<Awaited<ReturnType<typeof listAuthorityInteractions>>> =>
              r.status === 'fulfilled',
          )
          if (fulfilled.length > 0) {
            next.authorityInteractionCount = fulfilled.reduce((sum, r) => sum + r.value.items.length, 0)
          }
        }
      } catch {
        // Swallow — `next` carries whatever partial stats resolved before the failure; every
        // remaining tile below renders its own honest "not yet available" fallback.
      }

      if (!cancelled) {
        setStats(next)
        setLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [caseView.id, caseView.kind, caseView.productionId, caseView.legalEntityId, filings])

  // Case Lifecycle Progress Tracker (film credit lifecycle polish, GA/NY/LA) — film-only, mirroring
  // this same screen's Spend/Monetization/Dossier film-only gating. Every input is data this effect
  // already resolved above (no second fetch beyond the one new `listCaseDossiers` read); a piece
  // that hasn't resolved yet (still `loading`, or a failed source that degraded to `null`/`[]`)
  // simply renders its stage as not-yet-reached, never an error (`deriveLifecycle` has no failure
  // path of its own — it is a pure function over whatever partial `stats` this render pass has).
  const lifecycleSteps = useMemo(
    () =>
      caseView.kind === 'film'
        ? deriveLifecycle({
            filings: filings.map((f) => ({
              submissionOutcome: f.submissionOutcome,
              filedAt: f.filedAt,
              approvedAmount: f.approvedAmount,
              approvedCurrency: f.approvedCurrency,
            })),
            qualifiedSpendTotal: stats.totalQualifiedSpend,
            qualifiedSpendCurrency: stats.qualifiedSpendCurrency,
            certifiedInstruments: stats.certifiedInstruments,
            transfers: stats.transfers,
            dossiers: stats.dossiers,
          })
        : null,
    [caseView.kind, filings, stats],
  )

  // Roadmap #15 "case progress metrics" — a % complete / stage-count summary derived from the same
  // `lifecycleSteps` above (no second fetch, no new inputs): `deriveCaseProgress` is a pure reducer
  // over `LifecycleStep[]`, so it is only ever computed once `lifecycleSteps` itself resolves
  // (film-only in this slice, mirroring the tracker it complements).
  const caseProgress = useMemo(() => (lifecycleSteps ? deriveCaseProgress(lifecycleSteps) : null), [lifecycleSteps])

  const filingStatusCounts = filings.reduce<Record<string, number>>((acc, f) => {
    acc[f.currentState] = (acc[f.currentState] ?? 0) + 1
    return acc
  }, {})

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}>
      {lifecycleSteps ? (
        <section
          style={{
            padding: 'var(--iw-space-4)',
            background: 'var(--iw-surface)',
            border: '1px solid var(--iw-border)',
            borderRadius: 'var(--iw-radius-lg)',
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 'var(--iw-space-2)',
              marginBottom: 'var(--iw-space-3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)' }}>
              <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Case lifecycle</h2>
              <HelpBalloon id="overview.lifecycle" />
            </div>
            {caseProgress ? <CaseProgressIndicator progress={caseProgress} /> : null}
          </div>
          <LifecycleTracker steps={lifecycleSteps} />
        </section>
      ) : null}

      <section
        style={{
          padding: 'var(--iw-space-4)',
          background: 'var(--iw-surface)',
          border: '1px solid var(--iw-border)',
          borderRadius: 'var(--iw-radius-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-3)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Case summary</h2>
          <HelpBalloon id="overview.summary" />
        </div>
        <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-2)' }}>
          <SummaryRow term="Account">
            <EntityRef
              kind="account"
              id={caseView.accountId}
              name={caseContext?.accountName}
              href={`/clients/accounts/${caseView.accountId}`}
            />
          </SummaryRow>
          <SummaryRow term="Legal entity">
            <EntityRef
              kind="legalEntity"
              id={caseView.legalEntityId}
              name={caseContext?.legalEntityName}
              href={`/clients/legal-entities/${caseView.legalEntityId}`}
            />
            {stats.legalEntityJurisdiction ? (
              <span style={{ color: 'var(--iw-text-muted)' }}> · {label('jurisdiction', stats.legalEntityJurisdiction)}</span>
            ) : null}
          </SummaryRow>
          {caseView.kind === 'film' && caseView.productionId ? (
            <SummaryRow term="Production">
              <EntityRef
                kind="production"
                id={caseView.productionId}
                name={stats.productionTitle}
                href={`/productions/${caseView.productionId}`}
              />
              {stats.productionType ? (
                <span style={{ color: 'var(--iw-text-muted)' }}> · {label('productionType', stats.productionType)}</span>
              ) : null}
            </SummaryRow>
          ) : null}
          <SummaryRow term="Fiscal year">
            {caseView.fiscalYearId ? (
              <EntityRef kind="fiscalYear" id={caseView.fiscalYearId} name={caseContext?.fiscalYearLabel} />
            ) : (
              '—'
            )}
          </SummaryRow>
          <CaseOwnerRow caseId={caseView.id} />
        </dl>
      </section>

      <section
        style={{
          padding: 'var(--iw-space-4)',
          background: 'var(--iw-surface)',
          border: '1px solid var(--iw-border)',
          borderRadius: 'var(--iw-radius-lg)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--iw-space-2)', marginBottom: 'var(--iw-space-3)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--iw-size-md)' }}>Program filings</h2>
          <HelpBalloon id="overview.filings" />
        </div>
        <LifecycleStrip filings={filings} programRefs={programRefs} />
      </section>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--iw-space-3)' }}>
        <StatCard
          label="Filing status"
          value={
            filings.length === 0
              ? 'No filings yet'
              : Object.entries(filingStatusCounts)
                  .map(([state, count]) => `${label('programFilingState', state)} (${count})`)
                  .join(', ')
          }
        />
        <StatCard
          label="Certified credit total"
          value={
            loading
              ? 'Loading…'
              : stats.certifiedCreditTotal !== null
                ? formatAmount(stats.certifiedCreditTotal, stats.certifiedCreditCurrency)
                : 'Not yet certified'
          }
        />
        {caseView.kind === 'film' && caseView.productionId ? (
          <StatCard
            label="Total qualified spend"
            value={
              loading
                ? 'Loading…'
                : stats.totalQualifiedSpend !== null
                  ? formatAmount(stats.totalQualifiedSpend, stats.qualifiedSpendCurrency)
                  : 'Not yet recorded'
            }
          />
        ) : null}
        {caseView.kind === 'film' ? (
          <StatCard
            label="Net proceeds"
            subLabel="(transferred)"
            helpId="overview.netProceeds"
            value={
              loading
                ? 'Loading…'
                : stats.transferredTotal !== null
                  ? formatAmount(stats.transferredTotal, stats.transferredCurrency)
                  : 'Not yet monetized'
            }
          />
        ) : null}
        {caseView.kind === 'film' && caseView.productionId ? (
          <StatCard label="Spend lines" value={loading ? '…' : String(stats.spendLineCount ?? 0)} />
        ) : null}
        <StatCard label="Evidence items" value={loading ? '…' : String(stats.evidenceCount ?? 0)} />
        <StatCard label="Team members" value={loading ? '…' : String(stats.teamCount ?? 0)} />
        <StatCard label="Tasks" value={loading ? '…' : String(stats.taskCount ?? 0)} />
        {caseView.kind === 'film' ? (
          <StatCard
            label="Authority interactions"
            value={loading ? '…' : String(stats.authorityInteractionCount ?? 0)}
          />
        ) : null}
      </div>
    </div>
  )
}
